import { type TagAlertDetails, type ITag } from 'types/tag.type';
import { type IGetSingleDetailTagPayload } from 'types/payloads/tag.payload.types';
import BaseAPI from './base.api';

export default class TagAPI extends BaseAPI {
  get = async () => {
    const response = await this.API.get(`tag`);
    return response.data;
  };

  update = async ({ updatedTags }: { updatedTags: ITag[] }) => {
    const response = await this.API.patch('tag', {
      updatedTags,
    });
    return response.data;
  };

  getSingleTagDetails = async (
    tagId: string, // this is actually deviceId
    payload: IGetSingleDetailTagPayload,
    startDate?: Date,
    endDate?: Date
  ) => {
    const response = await this.API.post(`tag/${tagId}`, {
      ...payload,
      startDate,
      endDate,
    });
    return response.data;
  };

  getNotificationTagDetails = async ({
    deviceId,
    notificationTime,
    tagDetails,
  }: TagAlertDetails) => {
    const response = await this.API.post(`tag/notification-tags/${deviceId}`, {
      dateTime: notificationTime,
      tagDetails,
    });
    return response.data;
  };

  getTagGrainDetails = async (
    deviceId: string,
    payload: IGetSingleDetailTagPayload,
    startDate: Date,
    endDate: Date
  ) => {
    const response = await this.API.post(`tag/${deviceId}/grain`, {
      ...payload,
      startDate,
      endDate,
    });
    return response.data;
  };

  getTagThresholds = async () => {
    const response = await this.API.get('tag/thresholds');
    return response.data;
  };

  getBrakeTags = async (
    deviceId: string,
    brakeTags: string[],
    startDate: Date,
    endDate: Date,
    category: string
  ) => {
    const response = await this.API.post(`tag/getMovingAverage/${deviceId}`, {
      tagNames: brakeTags,
      startDate,
      endDate,
      category,
      device: deviceId,
    });
    return response.data;
  };

  getChassisTags = async (
    deviceId: string,
    chassisTags: string[],
    startDate: Date,
    endDate: Date,
    category: string
  ) => {
    const response = await this.API.post(`tag/getMovingAverage/${deviceId}`, {
      tagNames: chassisTags,
      startDate,
      endDate,
      category,
      device: deviceId,
    });
    return response.data;
  };

  getEngineTags = async (
    device: string,
    engineTags: string[],
    startDate: Date,
    endDate: Date,
    category: string
  ) => {
    const response = await this.API.post(`tag/getMovingAverage/${device}`, {
      tagNames: engineTags,
      startDate,
      endDate,
      category,
      device,
    });
    return response.data;
  };

  getVirtualLoadTestTags = async (
    device: string,
    startDate: Date,
    endDate: Date,
    onDataReceived?: (
      dataType: 'brakes' | 'chassis' | 'engine',
      data: any
    ) => void
  ) => {
    const brakeTags = [
      'j1939_parking_brake_switch',
      'cdl_service_brake_position',
      'cdl_parking_brake',
    ];

    const chassisTags = ['cdl_truck_payload', 'cdl_body_up_chassis'];

    const engineTags = [
      'j1939_engine_coolant_temperature',
      'j1939_engine_fuel_temperature_1',
      'j1939_engine_intake_manifold_1_temperature',
      'cdl_intake_manifold_air_temperature',
      'cdl_engine_oil_temperature',
    ];
    const tagRequests = [
      {
        apiCall: this.getBrakeTags,
        type: 'brakes' as const,
        tags: brakeTags,
        startDate,
        endDate,
        category: 'brakes',
        device,
      },
      {
        apiCall: this.getChassisTags,
        type: 'chassis' as const,
        tags: chassisTags,
        startDate,
        endDate,
        category: 'chassis',
        device,
      },
      {
        apiCall: this.getEngineTags,
        type: 'engine' as const,
        tags: engineTags,
        startDate,
        endDate,
        category: 'engine',
        device,
      },
    ];

    try {
      const promises = tagRequests.map(
        async ({
          apiCall,
          type,
          tags,
          startDate,
          endDate,
          category,
          device,
        }) => {
          try {
            const data = await apiCall(
              device,
              tags,
              startDate,
              endDate,
              category
            );
            console.log(`Got response from ${type}`, data);

            if (onDataReceived) {
              onDataReceived(type, data);
            }

            return { type, data };
          } catch (error) {
            console.error(`Error fetching ${type}`, error);
            if (onDataReceived) {
              onDataReceived(type, null);
            }
            return { type, data: null };
          }
        }
      );

      const results = await Promise.all(promises);
      return results;
    } catch (error) {
      console.error('Error in getVirtualLoadTestTags:', error);
      throw error;
    }
  };
}
